import { useNavigate } from 'react-router-dom'
import ViewAllButton from '@/components/ui/ViewAllButton'
import { useLanguage } from '@afsa/shared'
import { usePublicationHighlights } from '@/lib/api/hooks/publication'
import { useState, useMemo } from 'react'
import { type Publication } from '@/lib/api/types'
import { parseHtmlString, stripHtml } from '@/utils/helpers/html'

interface ResourceCardProps {
  title: string
  description: string
  date: string
  category: string
  downloadUrl?: string
  viewUrl?: string
  thumbnail?: string
  isExternalSource?: boolean
  externalLink?: string | null
}

const ResourceCard = ({ title, description, date, category, downloadUrl, viewUrl, thumbnail, isExternalSource, externalLink }: ResourceCardProps) => {
  const { t } = useLanguage()
  const [isDownloading, setIsDownloading] = useState(false)
  const defaultImage = "https://res.cloudinary.com/dlwegkhme/image/upload/v1755760288/Image-2_xbv8lj.png"
  const imageSrc = thumbnail || defaultImage

  const handleDownload = async (e: React.MouseEvent) => {
    e.preventDefault()
    if (downloadUrl && !isDownloading) {
      setIsDownloading(true)
      try {
        const { downloadPdf } = await import('../../utils/download')
        await downloadPdf(downloadUrl, title)
      } catch (error) {
        console.error('Download error:', error)
        window.open(downloadUrl, '_blank', 'noopener,noreferrer')
      } finally {
        setIsDownloading(false)
      }
    }
  }

  const handleView = (e: React.MouseEvent) => {
    e.preventDefault()
    if (viewUrl) {
      window.open(viewUrl, '_blank', 'noopener,noreferrer')
    }
  }

  const handleOpenExternalLink = (e: React.MouseEvent) => {
    e.preventDefault()
    const url = externalLink || downloadUrl
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer')
    }
  }

  return (
    <article className="bg-white overflow-hidden h-full flex flex-col">
      <div className="relative mb-4">
        <img
          src={imageSrc}
          alt={title}
          className="w-full h-64 object-cover"
          onError={(e) => {
            // Fallback to default image if the image fails to load
            if (e.currentTarget.src !== defaultImage) {
              e.currentTarget.src = defaultImage
            }
          }}
        />
      </div>

      <div className="space-y-3 flex-1 flex flex-col">
        <div className="flex items-center text-sm text-gray-600 font-satoshi">
          <span>{category}</span>
          <span className="mx-2">|</span>
          <span>{date}</span>
        </div>

        <h3 className="text-lg font-medium font-pt-serif text-gray-900 leading-tight">
          {title}
        </h3>

        <p className="text-gray-700 text-sm font-satoshi leading-relaxed flex-1">
          <span dangerouslySetInnerHTML={parseHtmlString(description)} />
        </p>

        <div className="flex items-center space-x-3 pt-2 mt-auto">
          {isExternalSource ? (
            // External source - show "Open Link" button
            <button
              onClick={handleOpenExternalLink}
              className="inline-flex items-center px-4 py-2 bg-wine-100 hover:bg-wine-200 text-white text-sm font-medium font-satoshi transition-colors duration-200 rounded-sm"
            >
              Open Link
            </button>
          ) : (
            // File upload - show download and view buttons
            <>
              {downloadUrl && (
                <button
                  onClick={handleDownload}
                  disabled={isDownloading}
                  className="inline-flex items-center px-4 py-2 bg-wine-100 hover:bg-wine-200 text-white text-sm font-medium font-satoshi transition-colors duration-200 rounded-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDownloading ? 'Downloading...' : t('download')}
                </button>
              )}

              {viewUrl && (
                <button
                  onClick={handleView}
                  className="inline-flex items-center px-4 py-2 text-gray-700 hover:text-gray-900 text-sm font-medium font-satoshi transition-colors duration-200 rounded-sm"
                >
                  {t('view')}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </article>
  )
}

interface ResourcesSectionProps {
  className?: string
}

const ResourcesSection = ({ className }: ResourcesSectionProps) => {
  const { t } = useLanguage()
  const navigate = useNavigate()

  // Fetch publications (highlights)
  const { data: highlightsData } = usePublicationHighlights()

  // Format date helper
  const formatDate = (dateString: string | null) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    const month = date.toLocaleDateString('en-US', { month: 'long' })
    const year = date.getFullYear()
    return `${month} ${year}`
  }

  // Convert category tag to friendly format (e.g., "LEGAL_FOUNDATION" -> "Legal Foundation")
  const formatCategory = (category: string | null | undefined): string => {
    if (!category) return "Legal Foundations"

    return category
      .toLowerCase()
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  // Default image constant
  const defaultImage = "https://res.cloudinary.com/dlwegkhme/image/upload/v1755760288/Image-2_xbv8lj.png"

  // Transform publications data
  const resources = useMemo(() => {
    // API returns a flat array of highlights in data.data
    const publications = highlightsData?.data || []

    return publications.map((pub: Publication) => ({
      title: pub.title,
      description: (stripHtml(pub.description || '').substring(0, 100) + '...') || "The African Space Policy Framework, endorsed by the African Union Heads of State in January 2016, provides a...",
      date: formatDate(pub.datePublished),
      category: formatCategory(pub.publicationCategory),
      downloadUrl: pub.publicationUrl || undefined,
      viewUrl: pub.publicationUrl || undefined,
      thumbnail: pub.coverImageUrl || defaultImage,
      isExternalSource: pub.isExternalSource || false,
      externalLink: pub.externalLink
    }))
  }, [highlightsData])

  const handleViewAllClick = () => {
    navigate('/resources')
  }

  return (
    <section className={`py-16 bg-white ${className}`} aria-labelledby="resources-title">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="flex items-center justify-between  mb-5 relative pb-2">
          <h2
            id="resources-title"
            className="text-lg font-medium text-gray-900 border-b-2 border-wine-100 relative z-10 pb-2 "
          >
            {t('key_resources')}
          </h2>

          <ViewAllButton onClick={handleViewAllClick} />

          {/* Full width gray underline */}
          <div className="absolute bottom-2.5 left-0 right-0 h-0.5 bg-gray-200"></div>
        </div>

        {/* Mobile horizontal scroll */}
        <div className="block md:hidden">
          {resources.length > 0 ? (
            <div className="flex overflow-x-auto gap-6 pb-4 snap-x snap-mandatory scrollbar-hide">
              {resources.map((resource, index) => (
                <div key={index} className="flex-none w-72 snap-start">
                  <ResourceCard
                    title={resource.title}
                    description={resource.description}
                    date={resource.date}
                    category={resource.category}
                    downloadUrl={resource.downloadUrl}
                    viewUrl={resource.viewUrl}
                    thumbnail={resource.thumbnail}
                    isExternalSource={resource.isExternalSource}
                    externalLink={resource.externalLink}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 text-sm">
              No resources available
            </div>
          )}
        </div>

        {/* Desktop grid */}
        <div className="hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {resources.length > 0 ? (
            <>
              {resources.map((resource, index) => (
                <ResourceCard
                  key={index}
                  title={resource.title}
                  description={resource.description}
                  date={resource.date}
                  category={resource.category}
                  downloadUrl={resource.downloadUrl}
                  viewUrl={resource.viewUrl}
                  thumbnail={resource.thumbnail}
                  isExternalSource={resource.isExternalSource}
                  externalLink={resource.externalLink}
                />
              ))}
            </>
          ) : (
            <div className="col-span-4 text-center py-8 text-gray-500 text-sm">
              No resources available
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

export default ResourcesSection